import {getFirebaseAuth} from '@local/logics/firebase/auth';
import {onAuthStateChanged} from 'firebase/auth';
import {FirebaseUser} from '@local/logics/firebase/user';
import {userActions} from '@local/redux/slice/user';
import {elasticsearchActions} from '@local/redux/slice/elasticsearch';
import {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import type {Unsubscribe} from 'firebase/auth';
import {LogoutInteractor} from '@local/logics/interactors/logoutInteractor';
import {apiEsToken} from '@local/apis/esToken';
import {RouterHolder} from '@local/logics/global/routerHolder';
import {eSearch} from '@local/logics/common/eSearch';
import {DispatchHolder} from '@local/logics/global/dispatchHolder';
import {EPeopleConverter} from '@local/elastic/converter/people';
import {TokenGetInteractor} from '@local/logics/interactors/tokenGetInteractor';
import {AuthError} from '@local/errors/authError';
import {snackbar} from '@local/logics/common/snackbar';
import {loginActions} from '@local/redux/slice/pages/login';

const auth = getFirebaseAuth();

// 認証不要なパスの一覧。
// もうちょっとグローバルっぽいところで定義したほうがいいかもだけどここでしか使わないからOK
const publicPaths: RegExp[] = [/^\/login(\/.+)?/, /^\/signup(\/.+)?/, /^\/forget-password(\/.+)?/];

// 認証情報を削除するアクション一覧
// 上記publicPaths以外にいて認証情報がなくなったら上から順に実行する
const authStateClearActions: {clear: () => any}[] = [userActions, elasticsearchActions, loginActions];

export const useAuth = () => {
  // できるだけ状態持たせたくなかったけどreduxのセキュリティとか色々考えるとこれが一番よさそう
  const [init, setInit] = useState(false);
  Listener.init = init;
  Listener.setInit = setInit;
  const router = useRouter();
  useEffect(() => {
    // auth変更時だけだとブラウザバックに対応できなかったからここでやる
    // なんかごちゃごちゃしてきたな。。。
    if (Listener.init && !FirebaseUser.user) {
      Listener.forceLogout().catch();
    }
  }, [router]);
  useEffect(() => {
    Listener.listen();
  }, []);
  return init;
};

class Listener {
  static init = false;
  static setInit?: (init: boolean) => any;
  private static unsubscribe: Unsubscribe;
  private static initialize = () => {
    // 初回の認証状態取得まではページコンポーネントを描画したくないのでこれでコントロール
    // あんまり悪影響ないけど極力再描画は避けたいからfalseの場合だけ呼ぶ
    if (!Listener.init) {
      Listener.setInit?.(true);
    }
  };
  static forceLogout = async () => {
    // 先に状態変更すると一瞬名前消えたりするからまず移動する
    const pathname = RouterHolder.router.pathname ?? '';
    if (!publicPaths.some(r => r.test(pathname))) {
      await RouterHolder.router.replace(`/login`);
      for (const action of authStateClearActions) {
        DispatchHolder.dispatch(action.clear());
      }
    }
  };

  static listen = () => {
    if (!!Listener.unsubscribe) {
      return;
    }
    Listener.unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      console.log('onAuthStateChanged', firebaseUser);
      const prevUser = FirebaseUser.user;
      FirebaseUser.user = firebaseUser;
      // リロードとか別タブでアクセスしても最初に発火するのはログイン後の状態なので、いきなりログアウト判定できる。
      if (!firebaseUser) {
        await Listener.forceLogout();
        Listener.initialize();
        return;
      }
      if (prevUser?.uid === firebaseUser.uid) {
        console.log('ignored');
        // ログイン後に二重で処理はしないようにする
        return;
      }
      console.log('user changed');
      try {
        const res = await apiEsToken();
        const {es_token, expires_at, people_id, organization_id, persona_id, user_id} = res.data;
        if (!organization_id) {
          // noinspection ExceptionCaughtLocallyJS
          throw new AuthError({code: 'unauthorized'});
        }
        TokenGetInteractor.setToken({es_token, expires_at, uid: firebaseUser.uid});
        const searched = await eSearch({
          index: 'people',
          query: {
            bool: {
              filter: [{term: {persona_id}}, {term: {organization_id}}],
            },
          },
          _source: ['email', 'name', 'system_roles'],
        });
        if (!searched) {
          // noinspection ExceptionCaughtLocallyJS
          throw new AuthError({code: 'user-not-found'});
        }
        const person = new EPeopleConverter().handle(searched).map(d => d.data)?.[0];
        if (!person) {
          // noinspection ExceptionCaughtLocallyJS
          throw new AuthError({code: 'user-not-found'});
        }
        if (!person.system_roles?.includes('sublease_admin')) {
          // noinspection ExceptionCaughtLocallyJS
          throw new AuthError({code: 'unauthorized'});
        }
        const {name, email} = person as {name?: string; email?: string};
        DispatchHolder.dispatch(
          userActions.set({user: {people_id, user_id, persona_id, organization_id, email, name: name ?? ''}})
        );
        Listener.initialize();
      } catch (e) {
        if (e instanceof AuthError) {
          switch (e.content.code) {
            case 'user-not-found':
              snackbar.error('ユーザー情報が見つかりませんでした。');
              break;
            case 'unauthorized':
              snackbar.error('権限がありません。');
              break;
          }
        } else {
          // ネットワーク繋がってない等のエラー
          // 頑張って分岐してsnackbar出しつつログイン画面に戻るかエラーページ表示がよさそうだけどもうちょいあとで
          // 取り急ぎログインに戻して初期化。
          console.error(e);
          snackbar.error('ログインできませんでした。');
        }
        await new LogoutInteractor().handle();
        Listener.initialize();
      }
    });
  };
}
